/**
 * division-registry — pure division-scoped visibility logic
 * (division-registry spec, task 4.2).
 *
 * Generalizes the legacy `ApplicantTypeVisibility { MRS | MISS | BOTH }` enum
 * into a data-driven selection over the configured Divisions (design
 * §Public-site & tab generalization). The wire shape becomes a nullable
 * `visibleDivisionKeys: [String!]`:
 *
 *   - null / empty list  ⇒ visible for ALL active Divisions (the `BOTH`
 *     semantics — Req 5.3, 5.5)
 *   - populated list      ⇒ visible only for the listed Division keys (Req 5.4)
 *
 * A `legacyVisibilityToDivisionKeys` helper read-through-maps existing
 * `applicantTypeVisibility` enum values onto the new shape
 * (`MRS → ["mrs"]`, `MISS → ["miss"]`, `BOTH → all`) so existing `FormField`
 * rows keep working during the transition — exactly the same strategy as the
 * `applicantType → divisionKey` migration in `./resolution`.
 *
 * This module is PURE — no I/O, no AWS SDK — so it can be exercised by `vitest`
 * (frontend) and `jest` (Lambda) against the same code.
 *
 * Validates: Requirements 5.3, 5.4, 5.5
 */

import type { Division } from './registryLogic';
import { backCompatMapping, type ApplicantType } from './resolution';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * The legacy GraphQL enum `ApplicantTypeVisibility { MRS | MISS | BOTH }` used
 * to scope a UI element to a single division or to all divisions
 * (Glossary: Applicant_Type_Visibility).
 */
export type ApplicantTypeVisibility = ApplicantType | 'BOTH';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Whether a `visibleDivisionKeys` selection means "all divisions" — i.e. it is
 * null, undefined, or empty (the `BOTH` semantics, Req 5.3/5.5). Whitespace-only
 * entries are ignored so a blank value never accidentally scopes the element.
 */
export function isAllDivisionsVisibility(
  visibleDivisionKeys: readonly string[] | null | undefined,
): boolean {
  if (visibleDivisionKeys == null) {
    return true;
  }
  return visibleDivisionKeys.every((k) => typeof k !== 'string' || k.trim() === '');
}

/** The Division keys that are currently active, derived from a division list. */
function activeDivisionKeys(activeDivisions: readonly Division[]): Set<string> {
  const keys = new Set<string>();
  for (const division of activeDivisions) {
    if (division.active) {
      keys.add(division.key);
    }
  }
  return keys;
}

// -----------------------------------------------------------------------------
// Visibility
// -----------------------------------------------------------------------------

/**
 * Whether a UI element carrying the given `visibleDivisionKeys` selection is
 * visible for the Division identified by `divisionKey`.
 *
 *   - When `visibleDivisionKeys` is null/empty, the element is visible for every
 *     active Division (the `BOTH` semantics): it is visible for `divisionKey`
 *     iff that key belongs to an active Division (Req 5.3, 5.5).
 *   - When `visibleDivisionKeys` is populated, the element is visible only for
 *     the listed keys: it is visible for `divisionKey` iff the list contains it
 *     (Req 5.4).
 *
 * @param visibleDivisionKeys the element's division selection (nullable list).
 * @param divisionKey         the Division key being tested.
 * @param activeDivisions     the year's Divisions; only `active` ones count for
 *                            the all-divisions case.
 */
export function isVisibleForDivision(
  visibleDivisionKeys: readonly string[] | null | undefined,
  divisionKey: string,
  activeDivisions: readonly Division[],
): boolean {
  if (isAllDivisionsVisibility(visibleDivisionKeys)) {
    return activeDivisionKeys(activeDivisions).has(divisionKey);
  }
  // Populated list: scoped to exactly the listed keys (Req 5.4). The non-null
  // assertion is safe — `isAllDivisionsVisibility` returns true for null.
  return (visibleDivisionKeys as readonly string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === divisionKey,
  );
}

// -----------------------------------------------------------------------------
// Legacy enum read-through mapping (Req 5.3)
// -----------------------------------------------------------------------------

/**
 * Map a legacy `ApplicantTypeVisibility` enum value onto the new
 * `visibleDivisionKeys` shape so existing rows keep working during the
 * transition:
 *
 *   - `MRS`  → `["mrs"]`  (scoped to the Back-Compat `mrs` Division)
 *   - `MISS` → `["miss"]` (scoped to the Back-Compat `miss` Division)
 *   - `BOTH` → `null`     (all divisions)
 *
 * The single-division keys come from the same {@link backCompatMapping} used by
 * the `applicantType` migration, keeping the two strangler paths consistent.
 * Returning `null` for `BOTH` yields the all-divisions semantics when fed to
 * {@link isVisibleForDivision}.
 */
export function legacyVisibilityToDivisionKeys(
  visibility: ApplicantTypeVisibility,
): string[] | null {
  if (visibility === 'BOTH') {
    return null;
  }
  return [backCompatMapping[visibility]];
}
